import React, { useCallback } from 'react'
import Linkify from 'react-linkify'

import { Button } from '@wartek-id/button'
import { Text } from '@wartek-id/text'

import YoutubePlayer from 'app/PelatihanMandiri/shared/YoutubePlayer'
import { youtubeDurationToHms, humanizeDate } from 'utils/datetime'
import { GATrackEvent } from 'utils/googleAnalytics'

import type { PlaylistVideo } from 'types/video-inspirasi'

interface VideoProps extends Omit<PlaylistVideo, 'thumbnailUrl'> {
  playlistId: number | string
  onShareClick: () => void
}

const Video = ({
  playlistId,
  title,
  videoId,
  description,
  duration,
  publisherName,
  publishedAt,
  onShareClick = () => {},
}: VideoProps) => {
  const [hasPlayed, setHasPlayed] = React.useState(false)
  const [isPlaying, setIsPlaying] = React.useState(false)
  const [secondCounter, setSecondCounter] = React.useState(0)

  const videoPlayedCallback = useCallback((target) => {
    if (target.data === 1) {
      setIsPlaying(true)
      setTimeout(() => {
        setHasPlayed(true)
      }, 100)
    } else if (target.data === 2 || target.data === 3) {
      setIsPlaying(false)
    }
  }, [])

  React.useEffect(() => {
    if (!hasPlayed && isPlaying) {
      GATrackEvent({
        eventName: 'learning_activity',
        eventParams: {
          event_trigger: 'inspirasi_video_click_play',
          action_type: 'play_video_inspirasi',
          meta: JSON.stringify({
            id: videoId,
            playlist_id: playlistId,
          }),
        },
        sendDefaultParams: true,
      })
    }
  }, [hasPlayed, isPlaying, playlistId, videoId])

  React.useEffect(() => {
    let interval
    if (isPlaying) {
      interval = setInterval(() => {
        setSecondCounter((prev) => {
          return prev + 1
        })
        GATrackEvent({
          eventName: 'stream_video_repository',
          eventParams: {
            playlist_name: title,
            video_id: videoId,
            second: secondCounter * 5,
          },
        })
      }, 5000)
    } else {
      if (typeof interval !== 'undefined') {
        clearInterval(interval)
      }
    }
    return () => {
      clearInterval(interval)
    }
  }, [isPlaying, secondCounter, title, videoId])

  return (
    <div>
      <div className="w-full">
        <YoutubePlayer videoId={videoId} onStateChange={videoPlayedCallback} />
      </div>
      <div className="p-4">
        <Text variant="heading-md" as="h1" className="mb-2">
          {title}
        </Text>
        <Text
          variant="body-sm"
          as="p"
          color="subdued"
          className="flex flex-wrap mb-3"
        >
          <span>{publisherName}</span>
          <span>&nbsp;&#8226;&nbsp;</span>
          <span>
            {humanizeDate(publishedAt).substr(
              0,
              humanizeDate(publishedAt).indexOf(',')
            )}
          </span>
          <span>&nbsp;&#8226;&nbsp;</span>
          <span>{youtubeDurationToHms(duration)}</span>
        </Text>
        <Text as="div" className="simple-wysiwyg-content break-words">
          <Linkify>
            {description.split('\n').map((item, key) => {
              return (
                <Text color="subdued" variant="body" as="span" key={key}>
                  {item}
                  <br />
                </Text>
              )
            })}
          </Linkify>
        </Text>

        <footer className="fixed bottom-0 left-0 w-screen">
          <div
            className="max-w-xl m-auto bg-white px-4 py-3"
            style={{
              boxShadow:
                '0px -2px 4px rgba(0, 0, 0, 0.04), 0px -4px 6px rgba(0, 0, 0, 0.04)',
            }}
          >
            <Button
              color="white"
              fullWidth
              onClick={() => {
                onShareClick()
              }}
            >
              <Text variant="action" as="span">
                Bagikan Video
              </Text>
            </Button>
          </div>
        </footer>
      </div>
    </div>
  )
}

export default Video
